/**
 * `t('surface-canvas')` — le proprieta' custom del marchio, con il nome
 * controllato dal compilatore.
 *
 * Il problema che questo file risolve e' piccolo e velenoso. `var(--beste-text-on-solid)`
 * e' scritto male — il nome vero e' `--beste-text-onSolid` — ma CSS non se ne
 * lamenta: la proprieta' diventa non valida al momento del calcolo, il colore
 * ripiega su quello ereditato, e il risultato e' un'etichetta grigia su un
 * bottone bronzo che nessun controllo automatico segnala e che si nota solo
 * guardando la schermata giusta con l'occhio giusto.
 *
 * Qui il nome di ogni token e' ricavato **dai tipi** di `@bestehr/brand`. Un
 * refuso non compila, e il giorno in cui il pacchetto del marchio aggiunge o
 * rinomina un token, i punti da aggiornare li elenca `vue-tsc` invece della
 * segnalazione di un collega sei mesi dopo.
 *
 * Il gioco di prestigio e' tutto nel generatore: `tools/build-tokens.mjs`
 * appiattisce gli oggetti unendo le chiavi con un trattino, quindi la stessa
 * trasformazione applicata ai tipi produce esattamente l'elenco dei nomi
 * esistenti — comprese le maiuscole in mezzo, che sono brutte ma sono quelle
 * vere e vanno rispettate, non indovinate.
 */

import { elevation, layout, motion, radius, space, typography, zIndex } from '@bestehr/brand';
import type { Theme } from '@bestehr/brand';

/**
 * `{ a: { b: string } }` diventa `'a-b'`.
 *
 * La ricorsione si ferma su tutto cio' che non e' un oggetto, che e' il punto
 * in cui il generatore CSS scrive il valore.
 */
type Flatten<T> = {
  [K in keyof T & (string | number)]: T[K] extends object
    ? `${K}-${Flatten<T[K]>}`
    : `${K}`;
}[keyof T & (string | number)];

/** I nomi generati da `typography.scale`: `font-body-size`, `font-h1-line`, ... */
type FontToken =
  | 'font-sans'
  | 'font-mono'
  | 'font-numeric'
  | `font-${keyof typeof typography.scale & string}-${'size' | 'line' | 'weight' | 'tracking'}`;

/**
 * Tutti i nomi che `tokens.css` dichiara davvero, e nessun altro.
 *
 * L'ordine ricalca quello del generatore, cosi' che un confronto fianco a
 * fianco fra questo tipo e `build-tokens.mjs` sia leggibile a occhio.
 */
export type BesteToken =
  | FontToken
  | `space-${keyof typeof space & (string | number)}`
  | `radius-${keyof typeof radius & string}`
  | `layout-${Flatten<typeof layout>}`
  | `elevation-${keyof typeof elevation & string}`
  | `z-${keyof typeof zIndex & string}`
  | `duration-${keyof typeof motion.duration & string}`
  | `ease-${keyof typeof motion.easing & string}`
  | Flatten<Theme>;

/**
 * Il riferimento CSS a un token del marchio.
 *
 * Nessun valore di ripiego come secondo argomento, di proposito: un ripiego
 * serve quando la variabile potrebbe non esserci, e qui non puo' mancare —
 * `tokens.css` e' importato una volta sola, in testa a tutto, e se mancasse
 * mancherebbe l'intero foglio. Un ripiego nasconderebbe quel guasto dietro un
 * colore plausibile.
 */
export function t(token: BesteToken): string {
  return `var(--beste-${token})`;
}
